import { z } from 'zod';

export const SEEDREAM = 'bytedance/seedream-5-pro';
export const SEEDANCE_FAST = 'bytedance/seedance-2.0-fast';
export const CLOUDFLARE_GEMINI = 'google/gemini-3.8-flash';
export const GEMINI_PRICING_VERSION = 'cloudflare-gemini-2026-10-02';
export const MEDIA_PRICING_VERSION = 'cloudflare-bytedance-2026-10-02';
export const CLOUDFLARE_MUSIC = 'minimax/music-2.6';
export const CLOUDFLARE_SPEECH = 'minimax/speech-2.8-turbo';
export const CLOUDFLARE_TRANSCRIPTION = '@cf/openai/whisper-large-v3-turbo';
export const AUDIO_PRICING_VERSION = 'cloudflare-audio-2026-10-02';
export const CLOUDFLARE_CLAUDE = 'anthropic/claude-sonnet-5';
export const CLAUDE_PRICING_VERSION = 'cloudflare-sonnet-5-2026-10-02';
// Pipeline 2, approved by the owner on October 2, 2026, all through Cloudflare Unified Billing.
export const H3 = 'minimax/h3';
export const SUNBURST = 'openai/gpt-image-2.5-sunburst';
export const GPT6_SOL = 'openai/gpt-6-sol';
export const GPT6_LUNA = 'openai/gpt-6-luna';
/** H3 at 768P is $0.08 a second; Sunburst is token-priced, about $0.05–0.30 an image. */
export const MEDIA2_PRICING_VERSION = 'cloudflare-h3-sunburst-2026-10-03';
/** Sol $2/$10 and Luna $0.10/$0.50 per million tokens, short context, September 22, 2026. */
export const OPENAI_PRICING_VERSION = 'cloudflare-gpt-6-2026-10-03';
export const OPENAI_RATES = {
  [GPT6_SOL]: { input: 2, cached: 0.2, output: 10 },
  [GPT6_LUNA]: { input: 0.1, cached: 0.01, output: 0.5 },
} as const;

const modelId = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-zA-Z0-9@._/-]+$/);
export const ProviderRouteSchema = z.discriminatedUnion('adapter', [
  z
    .object({
      adapter: z.literal('anthropic-unified'),
      model: z.literal(CLOUDFLARE_CLAUDE),
      pricingVersion: z.literal(CLAUDE_PRICING_VERSION),
      reservationMicros: z.number().int().positive().max(100_000_000),
    })
    .strict(),
  z
    .object({
      adapter: z.literal('cloudflare-audio'),
      model: z.enum([CLOUDFLARE_MUSIC, CLOUDFLARE_SPEECH, CLOUDFLARE_TRANSCRIPTION]),
      pricingVersion: z.literal(AUDIO_PRICING_VERSION),
    })
    .strict(),
  z
    .object({
      adapter: z.literal('google-unified'),
      model: z.literal(CLOUDFLARE_GEMINI),
      pricingVersion: z.literal(GEMINI_PRICING_VERSION),
      reservationMicros: z.number().int().positive().max(100_000_000),
    })
    .strict(),
  z
    .object({
      adapter: z.literal('cloudflare-media'),
      model: z.enum([SEEDREAM, SEEDANCE_FAST, H3, SUNBURST]),
      pricingVersion: z.enum([MEDIA_PRICING_VERSION, MEDIA2_PRICING_VERSION]),
      virtualAvatars: z.boolean().optional(),
    })
    .strict(),
  z
    .object({
      adapter: z.literal('openai-unified'),
      model: z.enum([GPT6_SOL, GPT6_LUNA]),
      pricingVersion: z.literal(OPENAI_PRICING_VERSION),
      reservationMicros: z.number().int().positive().max(100_000_000),
      reasoningEffort: z.enum(['low', 'medium', 'high', 'xhigh']).optional(),
    })
    .strict(),
  z
    .object({
      adapter: z.literal('google-native'),
      model: modelId,
    })
    .strict(),
  z
    .object({
      adapter: z.literal('chat'),
      model: modelId,
      // Explicit candidates also pin the rate card for automatic routing.
      candidates: z
        .array(
          z
            .object({
              model: modelId,
              inputMicrosPerToken: z.number().nonnegative().max(1000),
              outputMicrosPerToken: z.number().nonnegative().max(1000),
            })
            .strict(),
        )
        .min(1)
        .max(12),
      reservationMicros: z.number().int().positive().max(100_000_000),
      pricingVersion: z.string().min(1).max(100),
    })
    .strict(),
]);
export type ProviderRoute = z.infer<typeof ProviderRouteSchema>;
export const RoutingSchema = z
  .object({
    profile: z.string().min(1).max(80),
    version: z.string().min(1).max(80),
    transport: z.literal('cloudflare'),
    credentialPolicy: z.literal('cloudflare-only').optional(),
    route: ProviderRouteSchema,
  })
  .strict();
export type Routing = z.infer<typeof RoutingSchema>;
export const ProviderTraceSchema = z
  .object({
    transport: z.literal('cloudflare'),
    provider: z.string().min(1).max(80),
    actualModel: z.string().min(1).max(160),
    profile: z.string().min(1).max(80),
    version: z.string().min(1).max(80),
    pricingVersion: z.string().min(1).max(100),
    requestId: z.string().max(200).optional(),
    routingDecisionId: z.string().max(200).optional(),
  })
  .strict();
export type ProviderTrace = z.infer<typeof ProviderTraceSchema>;

// A completed synchronous generation is journalled before downloading its bytes.
export const ProviderOperationSchema = z
  .object({
    name: z.string().min(1).max(512),
    model: z.string(),
    mediaReceipt: z
      .object({
        url: z.string().url().max(8192),
        costMicros: z.number().int().nonnegative(),
        /** True when `costMicros` is the Gateway's recorded charge rather than an estimate. */
        charged: z.boolean().optional(),
        trace: ProviderTraceSchema,
      })
      .strict()
      .optional(),
    /** An accepted asynchronous generation (H3) that had not finished when the request returned. */
    pendingTask: z.string().min(1).max(200).optional(),
    /** The run's Gateway log, which is not named by its run ID, and the ref its metadata carries. */
    pendingLog: z
      .object({ id: z.string().min(8).max(128), ref: z.string().min(1).max(100) })
      .strict()
      .optional(),
  })
  .strict();
export type ProviderOperation = z.infer<typeof ProviderOperationSchema>;
